import { useListingMapCatalog } from './use-listing-map-catalog';
import { ListingThumbnail } from 'app/components/listing-thumbnail';
import { Column, Row } from 'app/design/primitives/box';
import { Map } from 'app/design/primitives/map';
import { Text } from 'app/design/primitives/text';
import { Thumbnail } from 'app/design/primitives/thumbnail';
import { useDebouncedState } from 'app/hooks/debounce';
import { getImageVariantUrl } from 'app/lib/image-variants';
import { getListingsFilters, useListingMap } from 'app/network/listing';
import { navigateToListing } from 'app/screens/billboard/navigator-helper';
import type { CustomTabFilters } from 'app/schemas/user';
import { createTheme as createDSTheme } from 'app/shopify';
import type { Address } from 'app/types/address';
import type { Listing, ListingsMapCluster, ListingsMapItem } from 'app/types/listing';
import { ListingSubtype, ListingType } from 'app/types/listing';
import type { FilterPreferences } from 'app/types/listings-filters';
import type { SpecificNavigationProp } from 'app/types/navigation';
import { useUISize } from 'app/utils/web-helpers';
import { IconHome, IconX } from '@tabler/icons-react-native';
import React, { memo, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, type LayoutChangeEvent, Platform, Pressable, View, type ViewStyle } from 'react-native';
import Animated, { Easing, useAnimatedStyle, withTiming } from 'react-native-reanimated';
import type { Details, Region } from 'react-native-maps';
import {
  createMapQueryCoverage,
  isCoordinateInMapViewport,
  regionToMapViewport,
  shouldRefreshMapQueryCoverage,
} from './listing-map-viewport';

const theme = createDSTheme();
const DEFAULT_REGION: Region = {
  latitude: 45.5019,
  longitude: -73.5674,
  latitudeDelta: 0.12,
  longitudeDelta: 0.12,
};
const GOLDEN_ANGLE_RADIANS = Math.PI * (3 - Math.sqrt(5));
const METERS_PER_LATITUDE_DEGREE = 111_320;
const MAP_QUERY_DEBOUNCE_MS = 120;
const MAP_COVERAGE_CHECK_THROTTLE_MS = 120;
const MAX_CLUSTER_FOCUS_ZOOM = 18;
const CLUSTER_FIT_PADDING_PX = 36;
const MIN_LISTINGS_PER_CLUSTER = 5;
const USER_CLUSTER_MARKER_SIZE = 58;
const MARKER_SPREAD_METERS_PER_PIXEL = 1.8;
const MARKER_SPREAD_GAP_PX = 8;
const MARKER_SELECTION_ANIMATION = { duration: 300, easing: Easing.out(Easing.cubic) };

interface SpreadPlacement {
  angle: number;
  radiusInMeters: number;
}

export interface ListingMapCatalogSync {
  listingIds: Listing['id'][] | undefined;
  listings: Listing[] | undefined;
  userId?: Listing['userId'];
}

interface ListingMapProps {
  appliedFilters: FilterPreferences;
  search: string;
  listingType: ListingType;
  forcedFilters: CustomTabFilters;
  isLoggedIn: boolean;
  navigation: SpecificNavigationProp<'all-listings'>;
  highlightedListingId?: Listing['id'];
  onMarkerHoverChange?: (listing?: Listing) => void;
  enabled?: boolean;
}

interface FocusedUser {
  id: Listing['userId'];
  listingIds: Listing['id'][];
  name: string;
  picture?: string;
}

interface UserListingCluster {
  id: string;
  baseCoordinate: { latitude: number; longitude: number };
  coordinate: { latitude: number; longitude: number };
  count: number;
  listingIds: Listing['id'][];
  publicLocationRadius: number;
  user: ListingsMapItem['listing']['user'];
  userId: Listing['userId'];
}

interface ListingMarkerItem {
  coordinate: { latitude: number; longitude: number };
  item: ListingsMapItem;
  markerSize: number;
}

function getMarkerSelectionState(listingIds: Listing['id'][], selectedListingIds: Set<Listing['id']>) {
  if (selectedListingIds.size === 0) return undefined;
  return listingIds.some((listingId) => selectedListingIds.has(listingId));
}

function useFilterScopedState<T>(filterKey: string) {
  const filterScope = useMemo(() => ({ filterKey }), [filterKey]);
  const [scopedState, setScopedState] = useState<{ filterScope: object; value: T }>();
  const value = scopedState?.filterScope === filterScope ? scopedState.value : undefined;
  const setValue = useCallback(
    (nextValue: T | undefined) =>
      setScopedState(nextValue === undefined ? undefined : { filterScope, value: nextValue }),
    [filterScope],
  );
  return [value, setValue] as const;
}

function useMarkerSelectionAnimation(selected?: boolean, isCluster = false) {
  return useAnimatedStyle(
    () => ({
      opacity: withTiming(selected === false ? 0.4 : 1, MARKER_SELECTION_ANIMATION),
      transform: [{ scale: withTiming(selected ? (isCluster ? 1.5 : 2.12) : 1, MARKER_SELECTION_ANIMATION) }],
    }),
    [selected],
  );
}

function getSortValue(appliedFilters: FilterPreferences, listingType: ListingType, forcedFilters: CustomTabFilters) {
  const listsServicesOnly =
    forcedFilters.subtypes?.length === 1 && forcedFilters.subtypes[0] === ListingSubtype.service;
  if (listsServicesOnly) return appliedFilters.sortServicesState[0];
  return listingType === ListingType.offer ? appliedFilters.sortOffersState[0] : appliedFilters.sortNeedsState[0];
}

function getRegionDeltaForZoom(zoom: number) {
  return 360 / 2 ** zoom;
}

function boundsForFit(bounds: ListingsMapCluster['bounds']) {
  const minimumDelta = getRegionDeltaForZoom(MAX_CLUSTER_FOCUS_ZOOM);
  const latitudeCenter = (bounds.north + bounds.south) / 2;
  const longitudeCenter = (bounds.east + bounds.west) / 2;
  const latitudeDelta = Math.max(minimumDelta, Math.abs(bounds.north - bounds.south));
  const longitudeDelta = Math.max(minimumDelta, Math.abs(bounds.east - bounds.west));

  return {
    north: latitudeCenter + latitudeDelta / 2,
    south: latitudeCenter - latitudeDelta / 2,
    east: longitudeCenter + longitudeDelta / 2,
    west: longitudeCenter - longitudeDelta / 2,
  };
}

function regionForBounds(bounds: ListingsMapCluster['bounds'], size: { width: number; height: number }): Region {
  const fitBounds = boundsForFit(bounds);
  const aspectRatio = size.width > 0 && size.height > 0 ? size.width / size.height : 1;
  const horizontalPaddingFactor =
    size.width > CLUSTER_FIT_PADDING_PX * 2 ? size.width / (size.width - CLUSTER_FIT_PADDING_PX * 2) : 1;
  const verticalPaddingFactor =
    size.height > CLUSTER_FIT_PADDING_PX * 2 ? size.height / (size.height - CLUSTER_FIT_PADDING_PX * 2) : 1;
  const paddedLatitudeDelta = Math.abs(fitBounds.north - fitBounds.south) * verticalPaddingFactor;
  const paddedLongitudeDelta = Math.abs(fitBounds.east - fitBounds.west) * horizontalPaddingFactor;
  const latitudeDelta = Math.max(paddedLatitudeDelta, paddedLongitudeDelta / Math.max(aspectRatio, 0.2));
  const longitudeDelta = Math.max(paddedLongitudeDelta, paddedLatitudeDelta * aspectRatio);

  return {
    latitude: (fitBounds.north + fitBounds.south) / 2,
    longitude: (fitBounds.east + fitBounds.west) / 2,
    latitudeDelta,
    longitudeDelta,
  };
}

function regionsAreNearlyEqual(left: Region, right: Region) {
  const latitudeTolerance = Math.max(0.000001, Math.max(left.latitudeDelta, right.latitudeDelta) * 0.0001);
  const longitudeTolerance = Math.max(0.000001, Math.max(left.longitudeDelta, right.longitudeDelta) * 0.0001);
  const directLongitudeDistance = Math.abs(left.longitude - right.longitude) % 360;
  const longitudeDistance = Math.min(directLongitudeDistance, 360 - directLongitudeDistance);
  return (
    Math.abs(left.latitude - right.latitude) <= latitudeTolerance &&
    longitudeDistance <= longitudeTolerance &&
    Math.abs(left.latitudeDelta - right.latitudeDelta) <= latitudeTolerance &&
    Math.abs(left.longitudeDelta - right.longitudeDelta) <= longitudeTolerance
  );
}

function getInitialRegion(appliedFilters: FilterPreferences): Region {
  const primaryAddress = getPrimaryAddress(appliedFilters);
  const latitude = primaryAddress?.latitude ?? appliedFilters.searchCenter.radiusCenterLatitude;
  const longitude = primaryAddress?.longitude ?? appliedFilters.searchCenter.radiusCenterLongitude;

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return DEFAULT_REGION;

  return {
    ...DEFAULT_REGION,
    latitude,
    longitude,
  };
}

function getPrimaryAddress(appliedFilters: FilterPreferences): Address | undefined {
  return appliedFilters.availableValues.addresses?.[0];
}

function getRegionForAddress(address: Address): Region {
  return {
    ...DEFAULT_REGION,
    latitude: address.latitude,
    longitude: address.longitude,
  };
}

function getUserDisplayName(user: ListingsMapItem['listing']['user'], t: (key: string) => string) {
  return (
    [user.profile.firstName, user.profile.lastName].filter(Boolean).join(' ').trim() || t('user-display-name-fallback')
  );
}

function getUserInitials(user: ListingsMapItem['listing']['user']) {
  const firstName = user.profile.firstName.trim();
  const lastName = user.profile.lastName.trim();
  return `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase() || '?';
}

function getPublicLocationKey(item: ListingsMapItem) {
  return `${item.publicLocation.latitude.toFixed(6)}:${item.publicLocation.longitude.toFixed(6)}`;
}

function getMapPointId(item: ListingsMapItem) {
  return item.id ?? `${item.listing.id}:${getPublicLocationKey(item)}`;
}

function getListingMarkerKey(item: ListingsMapItem, markerSize: number) {
  const { before, lender, picture, pictures, subtype, type } = item.listing;
  return [
    getMapPointId(item),
    pictures?.[0]?.url ?? picture ?? '',
    before ?? '',
    lender ? 'found' : 'available',
    subtype,
    type,
    markerSize,
  ].join('|');
}

function getUserLocationKey(item: ListingsMapItem) {
  return `${getPublicLocationKey(item)}:${item.listing.userId}`;
}

function getMarkerSize(stackTotal: number, isFocusedUserId: boolean) {
  const markerSize = isFocusedUserId ? 70 : 52;
  const stackReduction = stackTotal >= 30 ? 15 : stackTotal >= 16 ? 10 : stackTotal >= 8 ? 6 : 0;
  return Math.max(46, markerSize - stackReduction);
}

function getSpreadPlacement(
  publicLocationRadius: number,
  markerSize: number,
  stackTotal: number,
  stackIndex: number,
): SpreadPlacement {
  let remainingMarkers = stackTotal - 1;
  if (remainingMarkers <= 0) return { angle: 0, radiusInMeters: 0 };

  const markerDiameterInMeters = (markerSize + MARKER_SPREAD_GAP_PX) * MARKER_SPREAD_METERS_PER_PIXEL;
  const maxRadius = publicLocationRadius * 0.42;
  let ringRadius = markerDiameterInMeters * 0.72;
  let markerIndex = stackIndex - 1;
  let ringIndex = 0;

  while (remainingMarkers > 0) {
    const ringCapacity = Math.max(2, Math.floor((2 * Math.PI * ringRadius) / markerDiameterInMeters));
    if (markerIndex < ringCapacity) {
      const angleOffset = ringIndex * GOLDEN_ANGLE_RADIANS;
      return {
        angle: angleOffset + (2 * Math.PI * markerIndex) / ringCapacity,
        radiusInMeters: Math.min(maxRadius, ringRadius),
      };
    }
    markerIndex -= ringCapacity;
    remainingMarkers -= ringCapacity;
    ringRadius += markerDiameterInMeters;
    ringIndex += 1;
  }

  return { angle: stackIndex * GOLDEN_ANGLE_RADIANS, radiusInMeters: Math.min(maxRadius, ringRadius) };
}

function getSpreadCoordinate(item: ListingsMapItem, stackIndex: number, stackTotal: number, markerSize: number) {
  if (stackTotal <= 1) {
    return {
      latitude: item.publicLocation.latitude,
      longitude: item.publicLocation.longitude,
    };
  }

  if (stackIndex === 0) {
    return {
      latitude: item.publicLocation.latitude,
      longitude: item.publicLocation.longitude,
    };
  }

  const { angle, radiusInMeters } = getSpreadPlacement(item.publicLocation.radius, markerSize, stackTotal, stackIndex);
  const latitudeOffset = (radiusInMeters * Math.sin(angle)) / METERS_PER_LATITUDE_DEGREE;
  const longitudeScale = Math.max(0.2, Math.cos((item.publicLocation.latitude * Math.PI) / 180));
  const longitudeOffset = (radiusInMeters * Math.cos(angle)) / (METERS_PER_LATITUDE_DEGREE * longitudeScale);

  return {
    latitude: item.publicLocation.latitude + latitudeOffset,
    longitude: item.publicLocation.longitude + longitudeOffset,
  };
}

function getUserClusterSpreadCoordinate(
  cluster: UserListingCluster,
  stackIndex: number,
  stackTotal: number,
  markerSize: number,
) {
  if (stackTotal <= 1 || stackIndex === 0) return cluster.coordinate;

  const { angle, radiusInMeters } = getSpreadPlacement(
    cluster.publicLocationRadius,
    markerSize,
    stackTotal,
    stackIndex,
  );
  const latitudeOffset = (radiusInMeters * Math.sin(angle)) / METERS_PER_LATITUDE_DEGREE;
  const longitudeScale = Math.max(0.2, Math.cos((cluster.coordinate.latitude * Math.PI) / 180));
  const longitudeOffset = (radiusInMeters * Math.cos(angle)) / (METERS_PER_LATITUDE_DEGREE * longitudeScale);

  return {
    latitude: cluster.coordinate.latitude + latitudeOffset,
    longitude: cluster.coordinate.longitude + longitudeOffset,
  };
}

function getMapMarkerItems(items: ListingsMapItem[] | undefined, focusedUserId?: Listing['userId']) {
  const resolvedItems = items ?? [];
  const locationBuckets = new globalThis.Map<string, ListingsMapItem[]>();
  const userLocationBuckets = new globalThis.Map<string, ListingsMapItem[]>();
  for (const item of resolvedItems) {
    const locationKey = getPublicLocationKey(item);
    const locationBucket = locationBuckets.get(locationKey);
    if (locationBucket) {
      locationBucket.push(item);
    } else {
      locationBuckets.set(locationKey, [item]);
    }

    const userLocationKey = getUserLocationKey(item);
    const userLocationBucket = userLocationBuckets.get(userLocationKey);
    if (userLocationBucket) {
      userLocationBucket.push(item);
    } else {
      userLocationBuckets.set(userLocationKey, [item]);
    }
  }

  const userClusterKeys = new Set<string>();
  const userClustersByLocation = new globalThis.Map<string, UserListingCluster[]>();
  if (!focusedUserId) {
    for (const [key, bucket] of userLocationBuckets.entries()) {
      if (bucket.length < MIN_LISTINGS_PER_CLUSTER) continue;
      const [first] = bucket;
      if (!first) continue;
      userClusterKeys.add(key);
      const cluster = {
        id: key,
        baseCoordinate: {
          latitude: first.publicLocation.latitude,
          longitude: first.publicLocation.longitude,
        },
        coordinate: {
          latitude: first.publicLocation.latitude,
          longitude: first.publicLocation.longitude,
        },
        count: bucket.length,
        listingIds: [...new Set(bucket.map((bucketItem) => bucketItem.listing.id))].sort((a, b) => a.localeCompare(b)),
        publicLocationRadius: first.publicLocation.radius,
        user: first.listing.user,
        userId: first.listing.userId,
      };
      const locationKey = getPublicLocationKey(first);
      const clustersAtLocation = userClustersByLocation.get(locationKey);
      if (clustersAtLocation) {
        clustersAtLocation.push(cluster);
      } else {
        userClustersByLocation.set(locationKey, [cluster]);
      }
    }
  }

  const listingMarkers: ListingMarkerItem[] = [];
  const spreadUserClusters: UserListingCluster[] = [];
  for (const [locationKey, bucket] of locationBuckets.entries()) {
    const stableUserClusters = [...(userClustersByLocation.get(locationKey) ?? [])].sort((a, b) =>
      a.userId.localeCompare(b.userId),
    );
    const stableBucket = bucket
      .filter((item) => !userClusterKeys.has(getUserLocationKey(item)))
      .sort((a, b) => getMapPointId(a).localeCompare(getMapPointId(b)));
    const stackTotal = stableUserClusters.length + stableBucket.length;
    const markerSize = getMarkerSize(stackTotal, focusedUserId !== undefined);
    const spreadMarkerSize =
      stableUserClusters.length > 0 ? Math.max(markerSize, USER_CLUSTER_MARKER_SIZE) : markerSize;

    stableUserClusters.forEach((cluster, stackIndex) => {
      spreadUserClusters.push({
        ...cluster,
        coordinate: getUserClusterSpreadCoordinate(cluster, stackIndex, stackTotal, spreadMarkerSize),
      });
    });

    stableBucket.forEach((item, stackIndex) => {
      listingMarkers.push({
        item,
        coordinate: getSpreadCoordinate(item, stableUserClusters.length + stackIndex, stackTotal, spreadMarkerSize),
        markerSize,
      });
    });
  }

  return { listingMarkers, userClusters: spreadUserClusters };
}

export function useListingMapState({
  appliedFilters,
  forcedFilters,
  highlightedListingId,
  isLoggedIn,
  listingType,
  navigation,
  onMarkerHoverChange,
  enabled = true,
  search,
}: ListingMapProps) {
  const { t } = useTranslation('all-listings');
  const { isLargeUI } = useUISize();
  const primaryAddress = useMemo(() => getPrimaryAddress(appliedFilters), [appliedFilters]);
  const selectedFilterAddresses = useMemo(() => {
    const selectedAddressIds = new Set(appliedFilters.selectedAddresses);
    return (appliedFilters.availableValues.addresses ?? []).filter((address) => selectedAddressIds.has(address.id));
  }, [appliedFilters.availableValues.addresses, appliedFilters.selectedAddresses]);
  const initialRegion = useMemo(() => getInitialRegion(appliedFilters), [appliedFilters]);
  const sortValue = getSortValue(appliedFilters, listingType, forcedFilters);
  const filters = useMemo(
    () => getListingsFilters(appliedFilters.forQuery, search, listingType, isLoggedIn, forcedFilters, sortValue),
    [appliedFilters.forQuery, forcedFilters, isLoggedIn, listingType, search, sortValue],
  );
  const filterKey = JSON.stringify(filters);
  const [regionState, setRegion] = useDebouncedState<Region>(initialRegion, MAP_QUERY_DEBOUNCE_MS);
  const [selectedItem, setSelectedItem] = useFilterScopedState<ListingsMapItem>(filterKey);
  const [hoveredItem, setHoveredItem] = useFilterScopedState<ListingsMapItem>(filterKey);
  const [focusedUser, setFocusedUser] = useFilterScopedState<FocusedUser>(filterKey);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [mapQueryCoverage, setMapQueryCoverage] = useState(() => createMapQueryCoverage(initialRegion, 0));
  const centeredOnPrimaryAddress = useRef<Address['id'] | undefined>(undefined);
  const latestRegion = useRef(initialRegion);
  const lastCoverageCheckAt = useRef(0);

  useEffect(() => {
    if (!primaryAddress || centeredOnPrimaryAddress.current === primaryAddress.id) return;
    centeredOnPrimaryAddress.current = primaryAddress.id;
    const addressRegion = getRegionForAddress(primaryAddress);
    latestRegion.current = addressRegion;
    setMapQueryCoverage(createMapQueryCoverage(addressRegion, size.width));
    setRegion(addressRegion);
  }, [primaryAddress, setRegion, size.width]);

  const displayViewport = useMemo(
    () => regionToMapViewport(regionState.debounced, size.width),
    [regionState.debounced, size.width],
  );
  const mapViewport = mapQueryCoverage.viewport;
  const mapQuery = useListingMap(filters, mapViewport, {
    enabled: enabled && appliedFilters.isLoaded && isLoggedIn && size.width > 0,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    staleTime: 10_000,
  });
  const selectedOrHovered = hoveredItem ?? selectedItem;
  const selectedListingIds = useMemo(() => {
    const listingIds = [selectedOrHovered?.listing.id, highlightedListingId].filter(
      (listingId): listingId is Listing['id'] => listingId !== undefined,
    );
    return new Set(listingIds);
  }, [highlightedListingId, selectedOrHovered?.listing.id]);
  const { mapClusters, userClusters, listingItems, shouldShowLoading, currentCatalog, canUseCurrentCatalog } =
    useVisibleMapListings({
      mapQuery,
      focusedUser,
      displayViewport,
    });
  const { catalog, preserveCatalog } = useListingMapCatalog(
    currentCatalog,
    canUseCurrentCatalog,
    `${enabled}:${filterKey}`,
  );

  const refreshMapQueryCoverage = useEffectEvent(() => {
    if (!shouldRefreshMapQueryCoverage(mapQueryCoverage, regionState.debounced, size.width)) return;
    preserveCatalog();
    setMapQueryCoverage(createMapQueryCoverage(regionState.debounced, size.width));
  });
  useEffect(() => {
    const timeout = setTimeout(refreshMapQueryCoverage, 0);
    return () => clearTimeout(timeout);
  }, [regionState.debounced, size.width]);

  const openListing = useCallback(
    (listing: Listing) =>
      navigateToListing(navigation, listing.id, Boolean(listing.grouping?.totalOffers), undefined, true),
    [navigation],
  );
  const handleMarkerHoverChange = useCallback(
    (item: ListingsMapItem, isHovered: boolean) => {
      setHoveredItem(isHovered ? item : undefined);
      onMarkerHoverChange?.(isHovered ? item.listing : undefined);
    },
    [onMarkerHoverChange, setHoveredItem],
  );
  const handleMarkerPress = useCallback(
    (item: ListingsMapItem) => {
      if (isLargeUI) setSelectedItem(item);
      openListing(item.listing);
    },
    [isLargeUI, openListing, setSelectedItem],
  );

  const onRegionChangeComplete = useCallback(
    (nextRegion: Region, _details: Details) => {
      if (regionsAreNearlyEqual(latestRegion.current, nextRegion)) return;
      preserveCatalog();
      latestRegion.current = nextRegion;
      setRegion(nextRegion);
    },
    [preserveCatalog, setRegion],
  );
  const onRegionChange = useCallback(
    (nextRegion: Region, details?: Details) => {
      if (details?.isGesture === false) return;
      const now = Date.now();
      if (now - lastCoverageCheckAt.current < MAP_COVERAGE_CHECK_THROTTLE_MS) return;
      lastCoverageCheckAt.current = now;
      preserveCatalog();
      setMapQueryCoverage((coverage) =>
        shouldRefreshMapQueryCoverage(coverage, nextRegion, size.width)
          ? createMapQueryCoverage(nextRegion, size.width)
          : coverage,
      );
    },
    [preserveCatalog, size.width],
  );

  const focusCluster = useCallback(
    (cluster: ListingsMapCluster) => {
      preserveCatalog();
      setSelectedItem(undefined);
      setHoveredItem(undefined);
      const nextRegion = regionForBounds(cluster.bounds, size);
      latestRegion.current = nextRegion;
      setMapQueryCoverage(createMapQueryCoverage(nextRegion, size.width));
      setRegion(nextRegion);
    },
    [preserveCatalog, setHoveredItem, setRegion, setSelectedItem, size],
  );

  const focusUserCluster = useCallback(
    (cluster: UserListingCluster) => {
      preserveCatalog();
      setSelectedItem(undefined);
      setHoveredItem(undefined);
      const bounds = boundsForFit({
        north: cluster.baseCoordinate.latitude,
        south: cluster.baseCoordinate.latitude,
        east: cluster.baseCoordinate.longitude,
        west: cluster.baseCoordinate.longitude,
      });
      const region = regionForBounds(bounds, size);
      setFocusedUser({
        id: cluster.userId,
        listingIds: cluster.listingIds,
        name: getUserDisplayName(cluster.user, t),
        picture: cluster.user.profile.picture,
      });
      latestRegion.current = region;
      setMapQueryCoverage(createMapQueryCoverage(region, size.width));
      setRegion(region);
    },
    [preserveCatalog, setFocusedUser, setHoveredItem, setRegion, setSelectedItem, size, t],
  );

  const onLayout = useCallback(
    (event: LayoutChangeEvent) => {
      preserveCatalog();
      const nextSize = { width: event.nativeEvent.layout.width, height: event.nativeEvent.layout.height };
      setSize((currentSize) =>
        currentSize.width === nextSize.width && currentSize.height === nextSize.height ? currentSize : nextSize,
      );
    },
    [preserveCatalog],
  );

  const resetMap = useCallback(() => {
    setSelectedItem(undefined);
    setHoveredItem(undefined);
    setFocusedUser(undefined);
    centeredOnPrimaryAddress.current = undefined;
    latestRegion.current = initialRegion;
    setRegion(initialRegion);
    setMapQueryCoverage(createMapQueryCoverage(initialRegion, 0));
    setSize({ width: 0, height: 0 });
  }, [initialRegion, setFocusedUser, setHoveredItem, setRegion, setSelectedItem]);

  return {
    resetMap,
    onLayout,
    isLargeUI,
    size,
    onRegionChange,
    onRegionChangeComplete,
    regionState,
    selectedFilterAddresses,
    appliedFilters,
    focusedUser,
    mapClusters,
    focusCluster,
    selectedListingIds,
    userClusters,
    focusUserCluster,
    listingItems,
    handleMarkerHoverChange,
    handleMarkerPress,
    setFocusedUser,
    shouldShowLoading,
    selectedOrHovered,
    openListing,
    isLoggedIn,
    catalog,
  };
}

export function ListingMap(props: ListingMapProps | { state: ReturnType<typeof useListingMapState> }) {
  if ('state' in props) return <ListingMapView state={props.state} />;
  return <StandaloneListingMap {...props} />;
}

function StandaloneListingMap(props: ListingMapProps) {
  const state = useListingMapState(props);
  return <ListingMapView state={state} />;
}

function ListingMapView({ state }: { state: ReturnType<typeof useListingMapState> }) {
  const { t } = useTranslation('all-listings');
  const {
    onLayout,
    isLargeUI,
    size,
    onRegionChange,
    onRegionChangeComplete,
    regionState,
    selectedFilterAddresses,
    appliedFilters,
    focusedUser,
    mapClusters,
    focusCluster,
    selectedListingIds,
    userClusters,
    focusUserCluster,
    listingItems,
    handleMarkerHoverChange,
    handleMarkerPress,
    setFocusedUser,
    shouldShowLoading,
    selectedOrHovered,
    openListing,
    isLoggedIn,
  } = state;
  if (!isLoggedIn) {
    return <ListingMapEmpty title={t('map.signInToExplore')} />;
  }

  return (
    <View onLayout={onLayout} style={{ flex: 1, minHeight: isLargeUI ? 560 : 520 }}>
      <ListingMapCanvas
        size={size}
        onRegionChange={onRegionChange}
        onRegionChangeComplete={onRegionChangeComplete}
        regionState={regionState}
        isLargeUI={isLargeUI}
        selectedFilterAddresses={selectedFilterAddresses}
        appliedFilters={appliedFilters}
        focusedUser={focusedUser}
        mapClusters={mapClusters}
        focusCluster={focusCluster}
        selectedListingIds={selectedListingIds}
        userClusters={userClusters}
        focusUserCluster={focusUserCluster}
        listingItems={listingItems}
        handleMarkerHoverChange={handleMarkerHoverChange}
        handleMarkerPress={handleMarkerPress}
      />

      {focusedUser ? (
        <FocusedUserIndicator focusedUser={focusedUser} onClear={() => setFocusedUser(undefined)} />
      ) : null}

      {shouldShowLoading ? (
        <View style={styles.loadingPill}>
          <ActivityIndicator size="small" />
        </View>
      ) : null}

      {isLargeUI && selectedOrHovered ? (
        <ListingMapPreview item={selectedOrHovered} onPress={() => openListing(selectedOrHovered.listing)} />
      ) : null}
    </View>
  );
}

const ListingMapMarker = memo(function ListingMapMarkerComponent({
  coordinate,
  item,
  markerSize,
  onHoverChange,
  onPress,
  selected,
}: {
  coordinate: { latitude: number; longitude: number };
  item: ListingsMapItem;
  markerSize: number;
  onHoverChange: (item: ListingsMapItem, isHovered: boolean) => void;
  onPress: (item: ListingsMapItem) => void;
  selected?: boolean;
}) {
  const listing = item.listing;
  const thumbnailSize = Math.max(30, markerSize - theme.spacing['spacing.general.x1']);
  const animatedSelectionStyle = useMarkerSelectionAnimation(selected);

  return (
    <Map.Marker
      coordinate={coordinate}
      onHoverIn={() => onHoverChange(item, true)}
      onHoverOut={() => onHoverChange(item, false)}
      onPress={() => onPress(item)}
      selected={selected}
    >
      <Animated.View
        style={[
          styles.marker,
          {
            borderRadius: markerSize / 2,
            height: markerSize,
            padding: Math.max(2, markerSize * 0.05),
            width: markerSize,
          },
          animatedSelectionStyle,
          selected && styles.markerSelected,
        ]}
      >
        <ListingThumbnail
          before={listing.before}
          forcedObjectFit="cover"
          isFound={Boolean(listing.lender)}
          picture={listing.pictures?.[0]?.url ?? listing.picture}
          size={thumbnailSize}
          skipDonationOverlay
          subtype={listing.subtype}
          type={listing.type}
          fullRadius
        />
      </Animated.View>
    </Map.Marker>
  );
});

const OwnAddressMarker = memo(function OwnAddressMarkerComponent({ address }: { address: Address }) {
  return (
    <Map.Marker coordinate={{ latitude: address.latitude, longitude: address.longitude }}>
      <View style={styles.ownAddressMarker}>
        <IconHome color={theme.colors['surface.primary-neutral']} size={30} strokeWidth={2} />
      </View>
    </Map.Marker>
  );
});

const ListingMapUserCluster = memo(function ListingMapUserClusterComponent({
  cluster,
  onPress,
  selected,
}: {
  cluster: UserListingCluster;
  onPress: (cluster: UserListingCluster) => void;
  selected?: boolean;
}) {
  const picture = getImageVariantUrl(cluster.user.profile.picture, 'mini');
  const animatedSelectionStyle = useMarkerSelectionAnimation(selected, true);

  return (
    <Map.Marker coordinate={cluster.coordinate} onPress={() => onPress(cluster)} selected={selected}>
      <Animated.View style={[styles.userCluster, animatedSelectionStyle, selected && styles.markerSelected]}>
        {picture ? (
          <Thumbnail
            height={USER_CLUSTER_MARKER_SIZE - 8}
            source={{ uri: picture }}
            width={USER_CLUSTER_MARKER_SIZE - 8}
          />
        ) : (
          <Text color="surface.primary-neutral" fontSize={15} fontWeight="700">
            {getUserInitials(cluster.user)}
          </Text>
        )}
        <View style={styles.userClusterCount}>
          <Text color="surface.primary-neutral" fontSize={11} fontWeight="700">
            {cluster.count}
          </Text>
        </View>
      </Animated.View>
    </Map.Marker>
  );
});

const ListingMapCluster = memo(function ListingMapClusterComponent({
  cluster,
  onPress,
  selected,
}: {
  cluster: ListingsMapCluster;
  onPress: (cluster: ListingsMapCluster) => void;
  selected?: boolean;
}) {
  const animatedSelectionStyle = useMarkerSelectionAnimation(selected, true);

  return (
    <Map.Marker
      coordinate={{ latitude: cluster.latitude, longitude: cluster.longitude }}
      onPress={() => onPress(cluster)}
      selected={selected}
    >
      <Animated.View style={[styles.cluster, animatedSelectionStyle, selected && styles.markerSelected]}>
        <Text color="surface.primary-neutral" fontSize={14} fontWeight="700">
          {cluster.count}
        </Text>
      </Animated.View>
    </Map.Marker>
  );
});

function ListingMapCanvas({
  size,
  onRegionChange,
  onRegionChangeComplete,
  regionState,
  isLargeUI,
  selectedFilterAddresses,
  appliedFilters,
  focusedUser,
  mapClusters,
  focusCluster,
  selectedListingIds,
  userClusters,
  focusUserCluster,
  listingItems,
  handleMarkerHoverChange,
  handleMarkerPress,
}: {
  size: { width: number; height: number };
  onRegionChange: (nextRegion: Region, details?: Details) => void;
  onRegionChangeComplete: (nextRegion: Region, _details: Details) => void;
  regionState: { readonly actual: Region; readonly debounced: Region };
  isLargeUI: boolean;
  selectedFilterAddresses: Address[];
  appliedFilters: FilterPreferences;
  focusedUser: FocusedUser | undefined;
  mapClusters: ListingsMapCluster[];
  focusCluster: (cluster: ListingsMapCluster) => void;
  selectedListingIds: Set<string>;
  userClusters: UserListingCluster[];
  focusUserCluster: (cluster: UserListingCluster) => void;
  listingItems: ListingMarkerItem[];
  handleMarkerHoverChange: (item: ListingsMapItem, isHovered: boolean) => void;
  handleMarkerPress: (item: ListingsMapItem) => void;
}): React.ReactNode {
  return size.width > 0 && size.height > 0 ? (
    <Map
      controlledCamera
      height={size.height}
      minHeight={size.height}
      onRegionChange={onRegionChange}
      onRegionChangeComplete={onRegionChangeComplete}
      region={regionState.actual}
      width={size.width}
      style={{
        backgroundColor: theme.colors['surface.disabled'],
        borderColor: theme.colors['surface.inverse.tertiary-neutral'],
        borderRadius: isLargeUI ? theme.borderRadii['radius.lg'] : 0,
        borderWidth: 1,
        overflow: 'hidden',
      }}
    >
      <Map.DimOverlay fillColor={theme.colors['surface.overlay']} fillOpacity={0.1} />
      {isLargeUI
        ? selectedFilterAddresses.map((adr) => (
            <Map.Circle
              key={adr.id}
              fillOpacity={0.2}
              strokeColor={theme.colors['border.primary']}
              strokeWidth={2}
              fillColor={theme.colors['surface.secondary']}
              center={{
                latitude: adr.radiusCenterLatitude ?? adr.latitude,
                longitude: adr.radiusCenterLongitude ?? adr.longitude,
              }}
              radius={appliedFilters.searchDistance}
            />
          ))
        : null}

      {!focusedUser
        ? mapClusters.map((cluster) => (
            <ListingMapCluster
              key={`${cluster.id}:${cluster.count.toString()}`}
              cluster={cluster}
              onPress={focusCluster}
              selected={getMarkerSelectionState(cluster.listingIds, selectedListingIds)}
            />
          ))
        : null}

      {selectedFilterAddresses.map((ad) => (
        <OwnAddressMarker key={ad.id} address={ad} />
      ))}
      {!focusedUser
        ? userClusters.map((cluster) => (
            <ListingMapUserCluster
              key={`${cluster.id}:${cluster.count.toString()}:${cluster.user.profile.picture ?? ''}:${cluster.user.profile.firstName}:${cluster.user.profile.lastName}`}
              cluster={cluster}
              onPress={focusUserCluster}
              selected={getMarkerSelectionState(cluster.listingIds, selectedListingIds)}
            />
          ))
        : null}

      {listingItems.map(({ item, coordinate, markerSize }) => {
        const selected = getMarkerSelectionState([item.listing.id], selectedListingIds);
        return (
          <ListingMapMarker
            key={getListingMarkerKey(item, markerSize)}
            coordinate={coordinate}
            item={item}
            markerSize={markerSize}
            selected={selected}
            onHoverChange={handleMarkerHoverChange}
            onPress={handleMarkerPress}
          />
        );
      })}
    </Map>
  ) : null;
}

function FocusedUserIndicator({ focusedUser, onClear }: { focusedUser: FocusedUser; onClear: () => void }) {
  const { t } = useTranslation('all-listings');

  return (
    <View style={styles.focusedUserIndicator}>
      <Thumbnail
        height={32}
        source={focusedUser.picture ? { uri: getImageVariantUrl(focusedUser.picture, 'mini') } : undefined}
        width={32}
      />
      <Text color="surface.primary-neutral" fontSize={13} fontWeight="700" numberOfLines={1}>
        {t('focused-user-listings', { userName: focusedUser.name })}
      </Text>
      <Pressable onPress={onClear} style={styles.focusedUserClear}>
        <IconX color={theme.colors['surface.primary-neutral']} size={16} strokeWidth={2.5} />
      </Pressable>
    </View>
  );
}

function ListingMapPreview({ item, onPress }: { item: ListingsMapItem; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} style={styles.preview}>
      <ListingMapPreviewContent item={item} />
    </Pressable>
  );
}

function ListingMapPreviewContent({ item }: { item: ListingsMapItem }) {
  const listing = item.listing;
  const { t } = useTranslation('all-listings');
  return (
    <Row alignItems="center" gap="spacing.general.x3">
      <ListingThumbnail
        before={listing.before}
        forcedObjectFit="cover"
        isFound={Boolean(listing.lender)}
        picture={listing.pictures?.[0]?.url ?? listing.picture}
        size={68}
        subtype={listing.subtype}
        type={listing.type}
      />
      <Column flex={1} gap="spacing.general.x1">
        <Text fontSize={16} fontWeight="700" numberOfLines={2}>
          {listing.item.name}
        </Text>
        {listing.description ? (
          <Text color="text.secondary" fontSize={13} numberOfLines={3}>
            {listing.description}
          </Text>
        ) : null}
        {listing.distance !== undefined ? (
          <Text color="text.secondary" fontSize={13} numberOfLines={1}>
            {Math.max(0.1, listing.distance / 1000).toFixed(1)} km
          </Text>
        ) : null}
        <Text color="text.secondary" fontSize={12} numberOfLines={1}>
          {item.publicLocation.label ?? t('map.approximateLocation')}
        </Text>
      </Column>
    </Row>
  );
}

function ListingMapEmpty({ title }: { title: string }) {
  return (
    <Column alignItems="center" flex={1} justifyContent="center" minHeight={420} padding="spacing.general.x6">
      <Text color="text.secondary" textAlign="center" variant="bodyBase">
        {title}
      </Text>
    </Column>
  );
}

const styles = {
  marker: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.28,
    shadowRadius: 9,
    zIndex: 2,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  } as ViewStyle,
  markerSelected: {
    borderColor: theme.colors['border.secondary'],
    zIndex: 4,
  } as ViewStyle,
  ownAddressMarker: {
    alignItems: 'center',
    backgroundColor: theme.colors['surface.brand.default'],
    borderColor: theme.colors['surface.primary-neutral'],
    borderRadius: 45 / 2,
    borderWidth: 3,
    height: 45,
    width: 45,
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 5,
  } as ViewStyle,
  cluster: {
    alignItems: 'center',
    backgroundColor: theme.colors['text.primary'],
    borderColor: theme.colors['surface.primary-neutral'],
    borderRadius: 26,
    borderWidth: 3,
    height: 52,
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.24,
    shadowRadius: 8,
    width: 52,
    zIndex: 3,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  } as ViewStyle,
  userCluster: {
    alignItems: 'center',
    backgroundColor: theme.colors['surface.brand.default'],
    borderColor: theme.colors['surface.primary-neutral'],
    borderRadius: 29,
    borderWidth: 4,
    height: 58,
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.28,
    shadowRadius: 9,
    width: 58,
    zIndex: 4,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  } as ViewStyle,
  userClusterCount: {
    alignItems: 'center',
    backgroundColor: theme.colors['text.primary'],
    borderColor: theme.colors['surface.primary-neutral'],
    borderRadius: 11,
    borderWidth: 2,
    bottom: -3,
    height: 22,
    justifyContent: 'center',
    minWidth: 22,
    paddingHorizontal: 4,
    position: 'absolute',
    right: -3,
  } as ViewStyle,
  focusedUserIndicator: {
    alignItems: 'center',
    backgroundColor: theme.colors['text.brand'],
    borderColor: theme.colors['surface.primary-neutral'],
    borderRadius: 20,
    borderWidth: 2,
    flexDirection: 'row',
    gap: 8,
    left: 16,
    paddingHorizontal: 14,
    paddingVertical: 8,
    position: 'absolute',
    top: 16,
  } as ViewStyle,
  focusedUserClear: {
    alignItems: 'center',
    height: 22,
    justifyContent: 'center',
    width: 22,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  } as ViewStyle,
  loadingPill: {
    alignItems: 'center',
    backgroundColor: theme.colors['surface.primary-neutral'],
    borderRadius: 18,
    height: 36,
    justifyContent: 'center',
    position: 'absolute',
    right: 16,
    top: 16,
    width: 44,
  } as ViewStyle,
  preview: {
    backgroundColor: theme.colors['surface.primary-neutral'],
    borderRadius: 8,
    bottom: 16,
    left: 16,
    minWidth: 260,
    maxWidth: 560,
    padding: 12,
    position: 'absolute',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 16,
  } as ViewStyle,
};

function useVisibleMapListings({
  mapQuery,
  focusedUser,
  displayViewport,
}: {
  mapQuery: ReturnType<typeof useListingMap>;
  focusedUser: FocusedUser | undefined;
  displayViewport: ReturnType<typeof regionToMapViewport>;
}) {
  const focusedListingIds = focusedUser?.listingIds;
  const focusedListingIdSet = useMemo(() => new Set(focusedListingIds ?? []), [focusedListingIds]);
  const canSyncFocusedListingsFromCurrentData = useMemo(() => {
    if (!focusedListingIds) return false;
    const availableListingIds = new Set(mapQuery.data?.listings.map((item) => item.listing.id) ?? []);
    return focusedListingIds.every((id) => availableListingIds.has(id));
  }, [focusedListingIds, mapQuery.data?.listings]);
  const mapClusters = useMemo(
    () =>
      (mapQuery.data?.clusters ?? []).filter(
        (cluster) =>
          cluster.count >= MIN_LISTINGS_PER_CLUSTER && new Set(cluster.listingIds).size >= MIN_LISTINGS_PER_CLUSTER,
      ),
    [mapQuery.data?.clusters],
  );
  const clusteredPointIds = useMemo(() => {
    if (focusedUser) return new Set<string>();
    const pointIds = mapClusters.flatMap((cluster) => cluster.pointIds ?? []);
    if (pointIds.length > 0 || mapClusters.length === 0) return new Set(pointIds);

    const legacyClusteredListingIds = new Set(mapClusters.flatMap((cluster) => cluster.listingIds));
    return (mapQuery.data?.listings ?? []).reduce((clusteredIds, item) => {
      if (legacyClusteredListingIds.has(item.listing.id)) {
        clusteredIds.add(getMapPointId(item));
      }
      return clusteredIds;
    }, new Set<string>());
  }, [focusedUser, mapClusters, mapQuery.data?.listings]);
  const mapListingData = useMemo(() => {
    const listings = mapQuery.data?.listings ?? [];
    if (focusedListingIds) return listings.filter((item) => focusedListingIdSet.has(item.listing.id));
    return listings.filter((item) => !clusteredPointIds.has(getMapPointId(item)));
  }, [clusteredPointIds, focusedListingIdSet, focusedListingIds, mapQuery.data?.listings]);
  const { listingMarkers, userClusters } = useMemo(
    () => getMapMarkerItems(mapListingData, focusedUser?.id),
    [focusedUser?.id, mapListingData],
  );
  const listingItems = listingMarkers;
  const renderedCoordinatesByListingId = useMemo(() => {
    const coordinatesByListingId = new globalThis.Map<Listing['id'], { latitude: number; longitude: number }[]>();
    const addCoordinate = (listingId: Listing['id'], coordinate: { latitude: number; longitude: number }) => {
      const coordinates = coordinatesByListingId.get(listingId);
      if (coordinates) {
        coordinates.push(coordinate);
      } else {
        coordinatesByListingId.set(listingId, [coordinate]);
      }
    };

    listingMarkers.forEach(({ coordinate, item }) => addCoordinate(item.listing.id, coordinate));
    userClusters.forEach((cluster) => {
      cluster.listingIds.forEach((listingId) => addCoordinate(listingId, cluster.coordinate));
    });
    if (!focusedUser) {
      mapClusters.forEach((cluster) => {
        const coordinate = { latitude: cluster.latitude, longitude: cluster.longitude };
        cluster.listingIds.forEach((listingId) => addCoordinate(listingId, coordinate));
      });
    }

    return coordinatesByListingId;
  }, [focusedUser, listingMarkers, mapClusters, userClusters]);
  const visibleMapItems = useMemo(() => {
    const listings = mapQuery.data?.listings ?? [];
    const seenListingIds = new Set<Listing['id']>();
    return listings.filter((item) => {
      if (focusedListingIds && !focusedListingIdSet.has(item.listing.id)) return false;
      if (seenListingIds.has(item.listing.id)) return false;
      const renderedCoordinates = renderedCoordinatesByListingId.get(item.listing.id) ?? [item.publicLocation];
      if (!renderedCoordinates.some((coordinate) => isCoordinateInMapViewport(coordinate, displayViewport))) {
        return false;
      }
      seenListingIds.add(item.listing.id);
      return true;
    });
  }, [
    displayViewport,
    focusedListingIdSet,
    focusedListingIds,
    mapQuery.data?.listings,
    renderedCoordinatesByListingId,
  ]);
  const visibleListingIds = useMemo(() => {
    return visibleMapItems.map((item) => item.listing.id);
  }, [visibleMapItems]);
  const visibleListings = useMemo(() => {
    return visibleMapItems.map((item) => item.listing);
  }, [visibleMapItems]);
  const shouldShowLoading = mapQuery.isFetching && !canSyncFocusedListingsFromCurrentData;
  const currentCatalog = useMemo(
    () => ({
      listingIds: visibleListingIds,
      listings: visibleListings,
      userId: focusedUser?.id,
    }),
    [visibleListingIds, visibleListings, focusedUser?.id],
  );
  const canUseCurrentCatalog = !mapQuery.isPlaceholderData || canSyncFocusedListingsFromCurrentData;

  return { mapClusters, userClusters, listingItems, shouldShowLoading, currentCatalog, canUseCurrentCatalog };
}
